import { Alert, Select, SelectProps, SpaceBetween, Spinner, TextContent } from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDuration, intervalToDuration } from 'date-fns'
import { RestartSelectorOption } from '../../../../types';

const createOption = (startTimeStr: string, endTimeStr: number | undefined) => {
    const startTimeNum = Number(startTimeStr);
    const endTimeNum = endTimeStr || Date.now();
    const formattedDate = new Date(startTimeNum).toLocaleString();
    
    const duration = intervalToDuration({ start: startTimeNum, end: endTimeNum });
    const durationStr = formatDuration(duration, { delimiter: ', ', format: ['days', 'hours', 'minutes', 'seconds']});
    
    let label;
    if (endTimeStr) {
        label = `${formattedDate} (Completed - ${durationStr})`;
    } else {
        label = `${formattedDate} (In Progress - ${durationStr})`;
    }

    return {
        value: startTimeNum.toString(),
        label,
        timestampFrom: startTimeNum,
        timestampTo: endTimeNum
    };
}

interface Props {
    restarts: Record<number, number | undefined>;
    loading: boolean;
    error: Error | null;
    setTimestampFrom: (fromTimestamp: number) => void;
    setTimestampTo: (toTimestamp: number) => void;
}

export const RestartSelector: React.FC<Props> = ({
    restarts,
    loading,
    error,
    setTimestampFrom,
    setTimestampTo,
}) => {
    const [selectedOption, setSelectedOption] = useState<RestartSelectorOption | null>(null);

    const options = useMemo(() => {
        const entries = Object.entries(restarts);
        const optionsList: RestartSelectorOption[] = entries.map(([startTimeStr, endTimeStr]) => {
            return createOption(startTimeStr, endTimeStr);
        }).reverse();

        return optionsList;
    }, [restarts]);

    useEffect(() => {
        if (options.length > 0) {
            const currentValue = selectedOption?.value;
            const optionToUse = currentValue
                ? options.find(opt => opt.value === currentValue) || options[0]
                : options[0];

            setSelectedOption(optionToUse);
            setTimestampFrom(optionToUse.timestampFrom);
            setTimestampTo(optionToUse.timestampTo);
        }
    }, [options, selectedOption, setTimestampFrom, setTimestampTo]);

    const handleChange = useCallback((event: { detail: SelectProps.ChangeDetail }) => {
        const value = event.detail.selectedOption.value || '';
        const newSelectedOption = options.find(opt => opt.value === value);

        if (newSelectedOption) {
            setSelectedOption(newSelectedOption);
            setTimestampFrom(newSelectedOption.timestampFrom);
            setTimestampTo(newSelectedOption.timestampTo);
        }
    }, [options, setTimestampFrom, setTimestampTo]);

    if (error) {
        return (
            <>
                <TextContent>Restart History:</TextContent>
                <Alert type="error" header="Error">
                    {error.message || 'Failed to load Restart History'}
                </Alert>
            </>
        );
    }

    return (
        <>
            <SpaceBetween direction='horizontal' size='xxs'>
                <TextContent>Restart History:</TextContent>
                {loading && <Spinner size="normal" />}
            </SpaceBetween>
            <Select
                selectedOption={selectedOption}
                onChange={handleChange}
                options={options}
                placeholder="Select a restart"
                disabled={options.length === 0}
                empty="No restarts available"
                loadingText="Loading..."
            />
        </>
    );
};
